/**
 * V4 Editor API Client
 *
 * API functions for the V4 CMS editor module.
 * Uses the shared apiGet/apiPost helpers from client.ts.
 */

import { apiGet, apiPost, apiPatch, apiRequest } from './client';
import type {
  EditorContentResponse,
  EditorPagesResponse,
} from '../editor/types';

// =============================================================================
// CONTENT
// =============================================================================

/**
 * Get the V4 content model for a website.
 * Returns structured content + raw HTML sections for the editor.
 */
export async function getEditorContent(
  websiteId: string
): Promise<EditorContentResponse> {
  return apiGet<EditorContentResponse>(`/api/editor/${websiteId}/content`);
}

/**
 * Get full page HTML for iframe preview.
 * Returns raw HTML string (not JSON).
 */
export async function getEditorPreview(
  websiteId: string,
  pageSlug: string
): Promise<string> {
  const API_URL =
    import.meta.env.VITE_API_URL ||
    'https://niftee-orchestrator.jason-a6e.workers.dev';

  const response = await fetch(
    `${API_URL}/api/editor/${websiteId}/preview/${pageSlug}`,
    {
      headers: {
        // Include auth header
        ...getAuthHeaders(),
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to load preview: ${response.statusText}`);
  }

  return response.text();
}

/**
 * Get the page list for a website.
 */
export async function getEditorPages(
  websiteId: string
): Promise<EditorPagesResponse> {
  return apiGet<EditorPagesResponse>(`/api/editor/${websiteId}/pages`);
}

// =============================================================================
// STAGING SESSION
// =============================================================================

interface StartSessionResponse {
  success: boolean;
  stagingId?: string;
  staging?: { id: string; user_id?: string; user_name?: string; user_type?: string; changes?: Record<string, { newValue?: unknown }> };
  isNew?: boolean;
  /**
   * True when staged content differs from the published version. `changes`
   * only records element edits, so scaffold rebuilds (colour, logo height,
   * service area) and the contact-form patcher are invisible to it.
   */
  hasUnpublishedContent?: boolean;
  message?: string;
}

/**
 * Start or resume a staging session.
 * Acquires the edit lock for this user.
 */
export async function startStagingSession(
  websiteId: string,
  userId: string,
  userName: string
): Promise<StartSessionResponse> {
  return apiPost<StartSessionResponse>(`/api/staging/${websiteId}/start`, {
    userId,
    userName,
    userType: 'admin',
  });
}

/**
 * Suffix marking a change as an image's framing rather than its source.
 *
 * Mirrors IMAGE_POSITION_SUFFIX in the orchestrator's publish service. The
 * staging `changes` map is keyed by element id, so a swap and a reframe of the
 * same image would collide on one key and the later write would erase the
 * earlier. Keep the two in step.
 */
export const IMAGE_POSITION_SUFFIX = '#position';

/**
 * Suffix marking a change as a button's link target rather than its label.
 *
 * Mirrors CTA_HREF_SUFFIX in the orchestrator's publish service. A button has
 * two independently editable things and `changes` is keyed by element id, so
 * relabelling and re-pointing the same button would collide on one key.
 */
export const CTA_HREF_SUFFIX = '#href';

/**
 * Save a single element change to staging.
 */
export async function saveStagingElement(
  websiteId: string,
  elementId: string,
  oldValue: string,
  newValue: string
): Promise<{ success: boolean }> {
  console.log(`[API] saveStagingElement: websiteId=${websiteId}, elementId=${elementId}`);
  const result = await apiRequest<{ success: boolean }>(`/api/staging/${websiteId}/element`, {
    method: 'PATCH',
    body: JSON.stringify({ elementId, oldValue, newValue }),
  });
  console.log(`[API] saveStagingElement result:`, result);
  return result;
}

/**
 * Refresh the edit lock (heartbeat).
 */
export async function refreshStagingLock(
  websiteId: string
): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>(`/api/staging/${websiteId}/lock/refresh`);
}

/**
 * Release the edit lock.
 */
export async function releaseStagingLock(
  websiteId: string
): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>(`/api/staging/${websiteId}/lock`, {
    method: 'DELETE',
  });
}

// =============================================================================
// PUBLISH & DEPLOY
// =============================================================================

export interface DeployResponse {
  success: boolean;
  version_number?: number;
  version_id?: string;
  deployment_url?: string;
  changes_applied?: number;
  pages_deployed?: number;
  message?: string;
  error?: string;
  /** Publishing stopped because placeholder content is still on the site. */
  needsPlaceholderConfirmation?: boolean;
}

/**
 * Deploy staged changes: patch HTML + create version + deploy to Cloudflare Pages.
 */
export async function deployStagedChanges(
  websiteId: string,
  commitMessage?: string,
  acknowledgePlaceholders?: boolean
): Promise<DeployResponse> {
  return apiPost<DeployResponse>(`/api/staging/${websiteId}/deploy`, {
    commitMessage,
    acknowledgePlaceholders,
  });
}

// =============================================================================
// REVERT
// =============================================================================

/**
 * Revert all changes: abandon current session and start fresh from published version.
 */
export async function revertAllChanges(
  websiteId: string
): Promise<{ success: boolean; stagingId?: string; message?: string }> {
  return apiPost<{ success: boolean; stagingId?: string; message?: string }>(
    `/api/staging/${websiteId}/revert`
  );
}

// =============================================================================
// UNDO HISTORY
// =============================================================================

export interface UndoHistoryEntry {
  id: string;
  step_number: number;
  description: string;
  source: 'manual' | 'claude' | 'color' | 'reorder' | 'system';
  created_at: string;
}

export interface UndoHistoryResponse {
  count: number;
  entries: UndoHistoryEntry[];
}

export interface UndoResponse {
  success: boolean;
  undone?: string;
  source?: string;
  remainingUndoSteps?: number;
  error?: string;
}

/**
 * Undo the last edit step.
 * Restores staging_content to the state before the last edit.
 */
export async function undoLastEdit(
  websiteId: string
): Promise<UndoResponse> {
  return apiPost<UndoResponse>(`/api/staging/${websiteId}/undo`);
}

/**
 * Get the undo history (descriptions only, for UI display).
 */
export async function getUndoHistory(
  websiteId: string
): Promise<UndoHistoryResponse> {
  return apiGet<UndoHistoryResponse>(`/api/staging/${websiteId}/undo-history`);
}

// =============================================================================
// CLAUDE EDITOR CHAT
// =============================================================================

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface EditorAction {
  type: string;
  payload: Record<string, unknown>;
}

interface ActionResult {
  action: EditorAction;
  success: boolean;
  error?: string;
}

export interface ClaudeEditorResponse {
  success: boolean;
  reply: string;
  actions?: EditorAction[];
  actionResults?: ActionResult[];
  error?: string;
}

/**
 * Send a chat message to the Claude editor AI.
 * Returns Claude's response and any actions that were executed.
 */
export async function sendClaudeChatMessage(
  websiteId: string,
  message: string,
  selectedSection?: string,
  currentPage?: string,
  conversationHistory?: ChatMessage[]
): Promise<ClaudeEditorResponse> {
  return apiPost<ClaudeEditorResponse>(`/api/editor/${websiteId}/claude-chat`, {
    message,
    selectedSection,
    currentPage,
    conversationHistory: conversationHistory || [],
  });
}

// =============================================================================
// BUSINESS INFO (editable client data)
// =============================================================================

export interface BusinessInfoUpdate {
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  hasPhysicalLocation?: boolean;
  hours?: Record<string, { open: string; close: string; closed?: boolean }>;
  phone?: string;
  email?: string;
  showEmailOnWebsite?: boolean;
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
  yelp?: string;
  googleBusiness?: string;
  /** Nav logo height class (h-8 | h-10 | h-12 | h-14 | h-16) */
  logoHeightClass?: string;
  /** Service-area wording without the leading "Serving". '' clears the override. */
  serviceAreaLabel?: string;
}

/**
 * Update business contact info, address, and social links.
 * Saves to onboarding data and propagates changes to staging content.
 */
export async function updateBusinessInfo(
  websiteId: string,
  updates: BusinessInfoUpdate
): Promise<{ success: boolean; fieldsUpdated?: string[]; error?: string }> {
  return apiRequest<{ success: boolean; fieldsUpdated?: string[]; error?: string }>(
    `/api/editor/${websiteId}/business-info`,
    {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }
  );
}

// =============================================================================
// PAGE SEO
// =============================================================================

export interface PageSeoUpdate {
  pageSlug: string;
  title?: string;
  description?: string;
  keywords?: string;
}

/**
 * Update SEO metadata for a specific page.
 * Patches <title>, <meta description>, and <meta keywords> in staging HTML.
 */
export async function updatePageSeo(
  websiteId: string,
  updates: PageSeoUpdate
): Promise<{ success: boolean; fieldsUpdated?: string[]; error?: string }> {
  return apiRequest<{ success: boolean; fieldsUpdated?: string[]; error?: string }>(
    `/api/editor/${websiteId}/page-seo`,
    {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }
  );
}

/**
 * Switch theme between light and dark mode.
 * Applies 3-layer transformation to all staging pages.
 */
export async function switchTheme(
  websiteId: string,
  targetMode: 'light' | 'dark'
): Promise<{ success: boolean; themeMode?: string; pagesUpdated?: number; message?: string; error?: string }> {
  return apiRequest<{ success: boolean; themeMode?: string; pagesUpdated?: number; message?: string; error?: string }>(
    `/api/editor/${websiteId}/theme-switch`,
    {
      method: 'POST',
      body: JSON.stringify({ targetMode }),
    }
  );
}

/**
 * Upload or replace the business logo.
 * Uploads file to R2, updates onboarding data, and propagates to staging nav.
 */
export async function uploadLogo(
  websiteId: string,
  file: File
): Promise<{ success: boolean; logoUrl?: string; filename?: string; error?: string }> {
  const formData = new FormData();
  formData.append('file', file);

  // apiRequest, not a bare fetch: /api/editor/* is auth-gated, and it also
  // knows to leave Content-Type alone for FormData so the browser can set the
  // multipart boundary itself.
  const data = await apiRequest<{
    success: boolean;
    logoUrl?: string;
    filename?: string;
    error?: string;
  }>(`/api/editor/${websiteId}/upload-logo`, { method: 'POST', body: formData });

  if (!data.success) {
    throw new Error(data.error || 'Failed to upload logo');
  }

  return data;
}

/**
 * Remove the business logo. Reverts nav to text business name.
 */
export async function removeLogo(
  websiteId: string
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest<{ success: boolean; message?: string; error?: string }>(
    `/api/editor/${websiteId}/remove-logo`,
    { method: 'POST' }
  );
}

// =============================================================================
// PAGE MANAGEMENT
// =============================================================================

/**
 * Every operation here rewrites the scaffold (nav + footer) on EVERY page —
 * adding a page has to put the new nav link on all the others. The backend
 * preserves each page's <main>, so section content and prior edits survive,
 * but the whole site is touched. Callers should reload the content model
 * afterwards rather than patching local state.
 */

/** Mirrors PAGE_ROLES in the orchestrator's v4/page-roles.ts. */
export type PageRole = 'portfolio' | 'offers' | 'booking' | 'menu' | 'pricing' | 'shop';

export interface AddPageRequest {
  /** URL slug. Sanitized server-side to [a-z0-9-]. */
  slug: string;
  /** Page title, used for <title> and the nav label. */
  title: string;
  /** Page purpose — this is the AI generation prompt, so specificity matters. */
  description: string;
  /** Optional section type hints (hero, services, faq, ...). */
  sections?: string[];
  /** What the page is for; decides which page the nav button promotes. Inferred from the title when omitted. */
  role?: PageRole;
}

export interface AddPageResponse {
  success: boolean;
  generatedSlug?: string;
  cost?: number;
  tokens?: { input: number; output: number };
  error?: string;
}

/**
 * Add a page with AI-generated content.
 *
 * This is a Claude call — it costs real money (a few cents) and takes tens of
 * seconds. Capped at 10 pages per site by the backend.
 */
export async function addPage(
  websiteId: string,
  request: AddPageRequest
): Promise<AddPageResponse> {
  return apiRequest<AddPageResponse>(`/api/editor/${websiteId}/pages/add`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Remove a page and rebuild nav on the rest.
 * The backend refuses to remove home, or to drop below 2 visible nav pages.
 */
export async function removePage(
  websiteId: string,
  slug: string
): Promise<{ success: boolean; operation?: string; error?: string }> {
  return apiRequest<{ success: boolean; operation?: string; error?: string }>(
    `/api/editor/${websiteId}/pages/${slug}`,
    { method: 'DELETE' }
  );
}

/**
 * Reorder navigation.
 * @param order - ALL page slugs, in the desired order.
 */
export async function reorderNavigation(
  websiteId: string,
  order: string[]
): Promise<{ success: boolean; operation?: string; error?: string }> {
  return apiRequest<{ success: boolean; operation?: string; error?: string }>(
    `/api/editor/${websiteId}/navigation/reorder`,
    { method: 'POST', body: JSON.stringify({ order }) }
  );
}

/**
 * Rename a nav item or toggle its visibility.
 * Hiding a page leaves it published and reachable by URL — it only leaves the menu.
 */
export async function updateNavItem(
  websiteId: string,
  slug: string,
  updates: { label?: string; visible?: boolean }
): Promise<{ success: boolean; error?: string }> {
  return apiRequest<{ success: boolean; error?: string }>(
    `/api/editor/${websiteId}/navigation/${slug}`,
    { method: 'PATCH', body: JSON.stringify(updates) }
  );
}

/**
 * Rebuild header/footer/nav on all pages from current onboarding data.
 * An escape hatch for when scaffold and data have drifted apart.
 */
export async function rebuildScaffold(
  websiteId: string
): Promise<{ success: boolean; pagesRebuilt?: number; error?: string }> {
  return apiRequest<{ success: boolean; pagesRebuilt?: number; error?: string }>(
    `/api/editor/${websiteId}/scaffold-rebuild`,
    { method: 'POST' }
  );
}

// =============================================================================
// AI IMAGE GENERATION
// =============================================================================

/**
 * Staff-adjustable levers. Everything optional — omitted means "let the
 * client's brand profile decide", which is the common case.
 */
export interface GenerationLevers {
  setting?: 'home' | 'outdoor' | 'studio' | 'workplace' | 'community-space';
  mood?: 'warm-candid' | 'bright-energetic' | 'calm-serene' | 'professional';
  shot?: 'close-up' | 'medium' | 'wide';
  people?: 'none' | 'one' | 'pair' | 'small-group';
  /** Who the imagery should depict, in the client's own words. */
  representation?: string;
}

export interface GenerateImageRequest {
  /** Plain description. Pre-seeded from the slot's alt text, fully editable. */
  intent: string;
  levers?: GenerationLevers;
  sectionType?: string;
  aspect?: 'landscape' | 'square' | 'portrait';
  slot?: string;
  /** Build and price the prompt without generating. */
  previewOnly?: boolean;
  /** Optional image to build upon. */
  referenceUrl?: string;
  /** Required when referenceUrl is set — the server refuses without it. */
  referencePermissionConfirmed?: boolean;
}

export interface GenerateImageResponse {
  success: boolean;
  /** The assembled prompt actually sent to the image model. */
  prompt?: string;
  size?: string;
  estimatedCostUsd?: number;
  costUsd?: number;
  leversApplied?: string[];
  brandStyleAvailable?: boolean;
  usingReference?: boolean;
  usedReference?: boolean;
  url?: string;
  mediaId?: string;
  fromCache?: boolean;
  error?: string;
  contentPolicy?: boolean;
  needsPermissionConfirmation?: boolean;
}

/**
 * Build the prompt and price it without generating. Costs nothing.
 * This is the intended default while a user adjusts levers.
 */
export async function previewImagePrompt(
  websiteId: string,
  request: Omit<GenerateImageRequest, 'previewOnly'>
): Promise<GenerateImageResponse> {
  return apiRequest<GenerateImageResponse>(`/api/editor/${websiteId}/generate-image`, {
    method: 'POST',
    body: JSON.stringify({ ...request, previewOnly: true }),
  });
}

/**
 * Generate an image. Charges per image (~$0.013 landscape at low quality);
 * an identical brief returns the cached image at no cost.
 */
export async function generateImage(
  websiteId: string,
  request: Omit<GenerateImageRequest, 'previewOnly'>
): Promise<GenerateImageResponse> {
  return apiRequest<GenerateImageResponse>(`/api/editor/${websiteId}/generate-image`, {
    method: 'POST',
    body: JSON.stringify({ ...request, previewOnly: false }),
  });
}

// =============================================================================
// AUTH HELPER (mirrors client.ts pattern)
// =============================================================================

function getAuthHeaders(): Record<string, string> {
  const session = localStorage.getItem('niftee_session');
  if (session) {
    try {
      const { token } = JSON.parse(session);
      if (token) {
        return { Authorization: `Bearer ${token}` };
      }
    } catch {
      // Ignore
    }
  }

  const apiKey = localStorage.getItem('niftee_auth');
  if (apiKey) {
    try {
      const parsed = JSON.parse(apiKey);
      if (parsed.apiKey) {
        return { 'X-API-Key': parsed.apiKey };
      }
    } catch {
      // Ignore
    }
  }

  return {};
}

// =============================================================================
// GALLERY CONVERSION (ADR-009)
// =============================================================================

/** What converting a gallery will carry across, and what it will not. */
export interface GalleryConversionPlan {
  sectionId: string;
  pageSlug: string;
  imageCount: number;
  framedCount: number;
  captionCount: number;
  categories: string[];
  dropped: string[];
}

/**
 * Ask what conversion would do. Changes nothing.
 *
 * Conversion redesigns the section it touches, so the plan is always shown to a
 * human before anything happens.
 */
export async function previewGalleryConversion(
  websiteId: string,
  pageSlug: string,
  sectionId: string
): Promise<{ data: { plan: GalleryConversionPlan } }> {
  return apiPost(`/api/websites/${websiteId}/gallery/convert/preview`, { pageSlug, sectionId });
}

/** Convert the gallery to the editable model. */
export async function convertGallery(
  websiteId: string,
  pageSlug: string,
  sectionId: string
): Promise<{ data: { plan: GalleryConversionPlan; staleChangesDropped: number } }> {
  return apiPost(`/api/websites/${websiteId}/gallery/convert`, { pageSlug, sectionId });
}

// =============================================================================
// HEADER LINK + FORM EMBEDS
// =============================================================================

export interface PreheaderLink {
  text: string;
  shortText: string;
  url: string;
}

export interface SiteOverridesResponse {
  success: boolean;
  overrides?: {
    footerBg: string | null;
    navBg: string | null;
    preheaderLink?: PreheaderLink;
  };
}

/**
 * Read the site's explicit scaffold overrides.
 *
 * Served by the colours endpoint because that is where the step_3 overrides
 * (nav background, footer background, and now the header link) are already
 * read from — a second endpoint returning the same three strings would be
 * another thing to keep in step.
 */
export async function getSiteOverrides(websiteId: string): Promise<SiteOverridesResponse> {
  return apiGet<SiteOverridesResponse>(`/api/editor/${websiteId}/colors`);
}

/**
 * Set or clear the pre-header's custom link. '' clears a field.
 * Rebuilds the scaffold on every page, so the caller should reload the preview.
 */
export async function updatePreheaderLink(
  websiteId: string,
  link: Partial<PreheaderLink>
): Promise<{ success: boolean; pagesRebuilt: number; preheaderLink: PreheaderLink }> {
  return apiPatch(`/api/editor/${websiteId}/preheader-link`, link);
}

export interface FormEmbedDescriptor {
  provider: 'jotform' | 'google-forms' | 'sprout-studio';
  formId: string;
  host: string;
  height: number;
}

/**
 * Validate pasted embed code server-side and get back a descriptor.
 *
 * The provider allowlist lives in the worker and is deliberately NOT mirrored
 * here — a copy in the dashboard would be a second thing to keep in step, and
 * a client-side allowlist secures nothing anyway. This call is the only way to
 * turn a paste into something addable.
 */
export async function parseFormEmbedCode(
  websiteId: string,
  embedCode: string
): Promise<{ success: boolean; embed: FormEmbedDescriptor }> {
  return apiPost(`/api/editor/${websiteId}/form-embed/parse`, { embedCode });
}
